import React, { useState, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { motion } from "framer-motion";
import Http from "../../Http";
import { Blog_API } from "../../Helper";
import { Helmet } from "react-helmet";

const BlogMain = () => {
  const [blogs, setBlogs] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]); // Store all blogs for sidebar sections
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const blogsPerPage = 9; // Default blogs per page based on your API

  // Initial load - fetch categories and all blogs for sidebar
  useEffect(() => {
    fetchCategories();
    fetchAllBlogsForSidebar();
  }, []);

  // Fetch blogs when page or category changes
  useEffect(() => {
    fetchBlogs(currentPage, selectedCategory);
  }, [currentPage, selectedCategory]);

  // Fetch a limited number of all blogs for the sidebar sections
  const fetchAllBlogsForSidebar = async () => {
    setSidebarLoading(true);
    try {
      // Get a larger set of blogs for sidebar without category filtering
      const data = await Http.get(`${Blog_API}?page=1&limit=20`);
      setAllBlogs(data.blogs || []);
    } catch (error) {
      console.error("Error fetching sidebar blogs:", error);
    } finally {
      setSidebarLoading(false);
    }
  };

  // Fetch all available categories
  const fetchCategories = async () => {
    try {
      const data = await Http.get(`${Blog_API}?page=1&limit=50`); // Get a large sample to extract all categories
      const categoryMap = {};
      
      // Extract unique categories
      data.blogs.forEach(blog => {
        const category = blog.content_category;
        if (category) {
          categoryMap[category] = (categoryMap[category] || 0) + 1;
        }
      });
      
      const categoryArray = Object.keys(categoryMap).map(category => ({
        name: category,
        count: categoryMap[category]
      }));
      
      setCategories(categoryArray);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Count all blogs for a specific category
  const countBlogsByCategory = async (category) => {
    try {
      // If no category is selected, we can rely on the total_count from the API
      if (!category) {
        const data = await Http.get(`${Blog_API}?page=1&limit=1`);
        return data.total_count || 0;
      }
      
      // For category filtering, we need to count all blogs with this category
      // This is a temporary solution - ideally the API would support counting by category
      const data = await Http.get(`${Blog_API}?page=1&limit=100&category=${encodeURIComponent(category)}`);
      return data.blogs.filter(blog => blog.content_category === category).length;
    } catch (error) {
      console.error("Error counting blogs by category:", error);
      return 0;
    }
  };

  // Fetch blogs with pagination and category filtering
  const fetchBlogs = async (page, category) => {
    setLoading(true);
    try {
      // Add pagination and category parameters to API call
      let endpoint = `${Blog_API}?page=${page}&limit=${blogsPerPage}`;
      
      // Only add category parameter if one is selected
      if (category) {
        endpoint += `&category=${encodeURIComponent(category)}`;
      }
      
      const data = await Http.get(endpoint);
      
      // Filter blogs by category if needed (double-check server-side filtering)
      let filteredBlogs = data.blogs || [];
      if (category) {
        filteredBlogs = filteredBlogs.filter(blog => 
          blog.content_category === category
        );
      }
      
      setBlogs(filteredBlogs);
      
      // Get total count for the category or all blogs
      const totalBlogsCount = await countBlogsByCategory(category);
      setTotalBlogs(totalBlogsCount);
      
      // Calculate total pages based on filtered blogs count
      const calculatedTotalPages = Math.ceil(totalBlogsCount / blogsPerPage);
      setTotalPages(calculatedTotalPages > 0 ? calculatedTotalPages : 1);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setBlogs([]);
      setTotalBlogs(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Handle category click and filtering of blogs
  const handleCategoryClick = async (categoryName) => {
    setSelectedCategory(categoryName);
    setCurrentPage(1); // Reset to first page when changing category
    
    // Also update the total blogs count based on the selected category
    const totalBlogsCount = await countBlogsByCategory(categoryName);
    setTotalBlogs(totalBlogsCount);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when changing page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Get sorted blogs
  const sortedBlogs = blogs
    .sort((a, b) => new Date(b.content_date) - new Date(a.content_date));

  // Get new blogs for sidebar
  const newBlogs = allBlogs
    .sort((a, b) => new Date(b.content_date) - new Date(a.content_date))
    .slice(0, 3);

  // Get popular blogs for sidebar (using all blogs to ensure we have content)
  const popularBlogs = allBlogs.slice(0, 10);

  // Pagination component
  const Pagination = () => {
    const pageNumbers = [];
    const maxPageButtons = 5; // Max number of page buttons to show

    // Create array of visible page numbers
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    // Adjust if we're near the end
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center mt-8 mb-4">
        <nav className="flex items-center">
          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded-l-md border ${
              currentPage === 1 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-blue-600 hover:bg-blue-50'
            }`}
            aria-label="Previous Page"
          >
            <span className="sr-only">Previous</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Show first page and ellipsis if not near start */}
          {startPage > 1 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className={`px-4 py-2 border bg-white text-blue-600 hover:bg-blue-50`}
              >
                1
              </button>
              {startPage > 2 && (
                <span className="px-3 py-2 border bg-white text-gray-500">...</span>
              )}
            </>
          )}

          {/* Page numbers */}
          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`px-4 py-2 border ${
                currentPage === number
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-blue-600 hover:bg-blue-50'
              }`}
            >
              {number}
            </button>
          ))}

          {/* Show last page and ellipsis if not near end */}
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="px-3 py-2 border bg-white text-gray-500">...</span>
              )}
              <button
                onClick={() => handlePageChange(totalPages)}
                className={`px-4 py-2 border bg-white text-blue-600 hover:bg-blue-50`}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 rounded-r-md border ${
              currentPage === totalPages 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-blue-600 hover:bg-blue-50'
            }`}
            aria-label="Next Page"
          >
            <span className="sr-only">Next</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </nav>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>BidBox AI Blog – Insights on DSP and Advertising Trends</title>
        <meta name='description' content='Stay updated with the latest trends in DSP technology and targeted advertising through BidBox AIs expert blog articles and insights.' />
        <meta name='keywords' content='Programmatic advertising blog, DSP industry trends, digital marketing insights, ad tech news, AI in advertising, media buying strategies, advertising automation, real-time bidding updates, audience targeting tips, data-driven marketing, digital ad strategy, performance marketing techniques, online advertising trends, CTV and OTT advertising, brand growth through DSP.' />               
      </Helmet>
      <div
        className="relative h-96 bg-cover bg-center"
        style={{ backgroundImage: `url("/assets/blog.jpeg")` }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black opacity-50"></div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-center items-start pl-8 md:pl-16">
          <h1 className="text-white text-3xl md:text-5xl font-bold mb-4 mt-16">
            Resources 
          </h1>
          <p className="text-white text-lg md:text-xl mb-6">
            Insights. Trends. Innovation. Stay ahead with the latest in programmatic advertising.
          </p>
        </div>
      </div>
      <div className="container mx-auto p-4 py-20">
        <div className="flex flex-wrap">
          {/* Blog Content */}
          <div className="w-full md:w-3/4 pr-6 p-4 rounded-lg shadow-md">
            {/* Category indicator if selected */}
            {selectedCategory && (
              <div className="mb-6 px-4 py-3 bg-blue-50 border-l-4 border-blue-600 rounded">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-blue-800">Viewing: {selectedCategory}</h2>
                  <button 
                    onClick={() => handleCategoryClick(null)}
                    className="px-3 py-1 bg-white text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md text-sm"
                  >
                    View All Categories
                  </button>
                </div>
              </div>
            )}
            
            {loading ? (
              <Skeleton height={300} count={3} />
            ) : sortedBlogs.length === 0 ? (
              <div className="text-center py-12 text-gray-600">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-2 text-lg font-medium">No blogs found</h3>
                <p className="mt-1">We couldn't find any blogs in this category.</p>
                <div className="mt-6">
                  <button 
                    onClick={() => handleCategoryClick(null)} 
                    className="text-sm font-medium text-blue-600 hover:text-blue-500"
                  >
                    View all blogs instead
                  </button>
                </div>
              </div>
            ) : (
              <>
                {sortedBlogs.map((blog) => (
                  <motion.div
                    key={blog.id || blog.content_id}
                    className="mb-8"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <img 
                      src={blog.content_image} 
                      alt={blog.content_title} 
                      className="w-full h-60 sm:h-72 md:h-80 lg:h-[28rem] object-contain mb-4 mt-12" 
                    />
                    <div className="flex items-center text-gray-500 text-sm mb-2">
                      <span><i className="fa fa-comments font-bold text-lg">Published : </i> {blog.content_date}</span>
                      {blog.content_category && (
                        <span className="ml-4 px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs">
                          {blog.content_category}
                        </span>
                      )}
                    </div>
                    <h2 className="text-3xl font-semibold mb-2">{blog.content_title}</h2>
                    <div className="mt-6">
                      <a href={`/blog/${blog.content_url}`} className="hover:underline text-white bg-gradient-to-r from-blue-600 to-purple-500 px-4 py-2 rounded-full">
                        Read More
                      </a>
                    </div>
                  </motion.div>
                ))}
                
                {/* Pagination Controls */}
                {totalPages > 1 && <Pagination />}
                
                <div className="text-center text-gray-600 mt-4">
                  Showing page {currentPage} of {totalPages} ({totalBlogs} blogs)
                </div>
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-full md:w-1/4 mt-10">
            <div className="mb-8 bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
              <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600 pb-2">Categories</h3>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => handleCategoryClick(null)}
                    className={`flex justify-between items-center w-full text-gray-700 hover:text-blue-600 font-medium py-2 px-4 rounded-lg transition-colors ${
                      selectedCategory === null ? 'bg-blue-100 text-blue-600' : ''
                    }`}
                  >
                    <span  className="text-left break-words"> All Categories</span>
                    <span className="bg-gray-200 rounded-full px-2 py-1 text-xs text-gray-600">
                      {totalBlogs}
                    </span>
                  </button>
                </li>
                {categories.length === 0 ? (
                  <Skeleton count={5} height={30} />
                ) : (
                  categories.map((category) => (
                    <li key={category.name}>
                      <button
                        onClick={() => handleCategoryClick(category.name)}
                        className={`flex justify-between items-center w-full text-gray-700 hover:text-blue-600 font-medium py-2 px-4 rounded-lg transition-colors ${
                          selectedCategory === category.name ? 'bg-blue-100 text-blue-600' : ''
                        }`}
                      >
                        <span className="text-left break-words">{category.name}</span>
                        {/* <span className="bg-gray-200 rounded-full px-2 py-1 text-xs text-gray-600">
                          {category.count}
                        </span> */}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>

            {/* Sidebar - New Blogs */}
            <div className="mb-8 bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
              <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600 pb-2">New Blogs</h3>
              <ul>
                {sidebarLoading ? (
                  <Skeleton count={3} height={80} />
                ) : (
                  newBlogs.map((blog) => (
                    <motion.li
                      key={blog.id || blog.content_id}
                      className="mb-4 flex"
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <a href={`/blog/${blog.content_url}`} className="flex items-start w-full">
                        <img src={blog.content_image} alt={blog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
                        <div>
                          <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors">{blog.content_title}</h4>
                          <p className="text-gray-500 text-sm">{blog.content_date}</p>
                        </div>
                      </a>
                    </motion.li>
                  ))
                )}
              </ul>
            </div>

            {/* Sidebar - Popular Posts */}
            <div className="bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
              <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600 pb-2">Popular Posts</h3>
              <ul>
                {sidebarLoading ? (
                  <Skeleton count={3} height={80} />
                ) : (
                  popularBlogs.map((blog) => (
                    <motion.li
                      key={blog.id || blog.content_id}
                      className="mb-4 flex"
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <a href={`/blog/${blog.content_url}`} className="flex items-start w-full">
                        <img src={blog.content_image} alt={blog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
                        <div>
                          <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors">{blog.content_title}</h4>
                          <p className="text-gray-500 text-sm">{blog.content_date}</p>
                        </div>
                      </a>
                    </motion.li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogMain;

//correct code

// import React, { useState, useEffect } from "react";
// import Skeleton from "react-loading-skeleton";
// import 'react-loading-skeleton/dist/skeleton.css';
// import { motion } from "framer-motion";
// import Http from "../../Http";
// import { Blog_API } from "../../Helper";
// import { Helmet } from "react-helmet";

// const BlogMain = () => {
//   const [blogs, setBlogs] = useState([]);
//   const [allBlogs, setAllBlogs] = useState([]); // Store all blogs for sidebar sections
//   const [categories, setCategories] = useState([]);
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [sidebarLoading, setSidebarLoading] = useState(true);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(0);
//   const [totalBlogs, setTotalBlogs] = useState(0);
//   const blogsPerPage = 9; // Default blogs per page based on your API

//   // Initial load - fetch categories and all blogs for sidebar
//   useEffect(() => {
//     fetchCategories();
//     fetchAllBlogsForSidebar();
//   }, []);

//   // Fetch blogs when page or category changes
//   useEffect(() => {
//     fetchBlogs(currentPage, selectedCategory);
//   }, [currentPage, selectedCategory]);

//   // Fetch a limited number of all blogs for the sidebar sections
//   const fetchAllBlogsForSidebar = async () => {
//     setSidebarLoading(true);
//     try {
//       // Get a larger set of blogs for sidebar without category filtering
//       const data = await Http.get(`${Blog_API}?page=1&limit=20`);
//       setAllBlogs(data.blogs || []);
//     } catch (error) {
//       console.error("Error fetching sidebar blogs:", error);
//     } finally {
//       setSidebarLoading(false);
//     }
//   };

//   // Fetch all available categories
//   const fetchCategories = async () => {
//     try {
//       const data = await Http.get(`${Blog_API}?page=1&limit=50`); // Get a large sample to extract all categories
//       const categoryMap = {};
      
//       // Extract unique categories
//       data.blogs.forEach(blog => {
//         const category = blog.content_category;
//         if (category) {
//           categoryMap[category] = (categoryMap[category] || 0) + 1;
//         }
//       });
      
//       const categoryArray = Object.keys(categoryMap).map(category => ({
//         name: category,
//         count: categoryMap[category]
//       }));
      
//       setCategories(categoryArray);
//     } catch (error) {
//       console.error("Error fetching categories:", error);
//     }
//   };

//   // Fetch blogs with pagination and category filtering
//   const fetchBlogs = async (page, category) => {
//     setLoading(true);
//     try {
//       // Add pagination and category parameters to API call
//       let endpoint = `${Blog_API}?page=${page}`;
//       if (category) {
//         endpoint += `&category=${encodeURIComponent(category)}`;
//       }
        
//       const data = await Http.get(endpoint);
      
//       // Filter blogs by category if needed (double-check server-side filtering)
//       let filteredBlogs = data.blogs;
//       if (category) {
//         filteredBlogs = filteredBlogs.filter(blog => 
//           blog.content_category === category
//         );
//       }
      
//       setBlogs(filteredBlogs);
      
//       // Use the filtered count for total blogs and pagination
//       const filteredCount = category ? filteredBlogs.length : (data.total_count || 0);
//       setTotalBlogs(filteredCount);
      
//       // Calculate total pages based on filtered blogs count
//       const calculatedTotalPages = Math.ceil(filteredCount / blogsPerPage);
//       setTotalPages(calculatedTotalPages > 0 ? calculatedTotalPages : 1);
//     } catch (error) {
//       console.error("Error fetching blogs:", error);
//       setBlogs([]);
//       setTotalBlogs(0);
//       setTotalPages(1);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Handle category click and filtering of blogs
//   const handleCategoryClick = (categoryName) => {
//     setSelectedCategory(categoryName);
//     setCurrentPage(1); // Reset to first page when changing category
//   };

//   // Handle page change
//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//     // Scroll to top when changing page
//     window.scrollTo({
//       top: 0,
//       behavior: 'smooth'
//     });
//   };

//   // Get sorted blogs
//   const sortedBlogs = blogs
//     .sort((a, b) => new Date(b.content_date) - new Date(a.content_date));

//   // Get new blogs for sidebar
//   const newBlogs = allBlogs
//     .sort((a, b) => new Date(b.content_date) - new Date(a.content_date))
//     .slice(0, 3);

//   // Get popular blogs for sidebar (using all blogs to ensure we have content)
//   const popularBlogs = allBlogs.slice(0, 10);

//   // Pagination component
//   const Pagination = () => {
//     const pageNumbers = [];
//     const maxPageButtons = 5; // Max number of page buttons to show

//     // Create array of visible page numbers
//     let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
//     let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
//     // Adjust if we're near the end
//     if (endPage - startPage + 1 < maxPageButtons) {
//       startPage = Math.max(1, endPage - maxPageButtons + 1);
//     }

//     for (let i = startPage; i <= endPage; i++) {
//       pageNumbers.push(i);
//     }

//     return (
//       <div className="flex justify-center mt-8 mb-4">
//         <nav className="flex items-center">
//           <button
//             onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
//             disabled={currentPage === 1}
//             className={`px-3 py-2 rounded-l-md border ${
//               currentPage === 1 
//                 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
//                 : 'bg-white text-blue-600 hover:bg-blue-50'
//             }`}
//             aria-label="Previous Page"
//           >
//             <span className="sr-only">Previous</span>
//             <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
//             </svg>
//           </button>

//           {/* Show first page and ellipsis if not near start */}
//           {startPage > 1 && (
//             <>
//               <button
//                 onClick={() => handlePageChange(1)}
//                 className={`px-4 py-2 border bg-white text-blue-600 hover:bg-blue-50`}
//               >
//                 1
//               </button>
//               {startPage > 2 && (
//                 <span className="px-3 py-2 border bg-white text-gray-500">...</span>
//               )}
//             </>
//           )}

//           {/* Page numbers */}
//           {pageNumbers.map(number => (
//             <button
//               key={number}
//               onClick={() => handlePageChange(number)}
//               className={`px-4 py-2 border ${
//                 currentPage === number
//                   ? 'bg-blue-600 text-white border-blue-600'
//                   : 'bg-white text-blue-600 hover:bg-blue-50'
//               }`}
//             >
//               {number}
//             </button>
//           ))}

//           {/* Show last page and ellipsis if not near end */}
//           {endPage < totalPages && (
//             <>
//               {endPage < totalPages - 1 && (
//                 <span className="px-3 py-2 border bg-white text-gray-500">...</span>
//               )}
//               <button
//                 onClick={() => handlePageChange(totalPages)}
//                 className={`px-4 py-2 border bg-white text-blue-600 hover:bg-blue-50`}
//               >
//                 {totalPages}
//               </button>
//             </>
//           )}

//           <button
//             onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
//             disabled={currentPage === totalPages}
//             className={`px-3 py-2 rounded-r-md border ${
//               currentPage === totalPages 
//                 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
//                 : 'bg-white text-blue-600 hover:bg-blue-50'
//             }`}
//             aria-label="Next Page"
//           >
//             <span className="sr-only">Next</span>
//             <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
//             </svg>
//           </button>
//         </nav>
//       </div>
//     );
//   };

//   return (
//     <>
//       <Helmet>
//         <title>BidBox AI Blog – Insights on DSP and Advertising Trends</title>
//         <meta name='description' content='Stay updated with the latest trends in DSP technology and targeted advertising through BidBox AIs expert blog articles and insights.' />
//         <meta name='keywords' content='Programmatic advertising blog, DSP industry trends, digital marketing insights, ad tech news, AI in advertising, media buying strategies, advertising automation, real-time bidding updates, audience targeting tips, data-driven marketing, digital ad strategy, performance marketing techniques, online advertising trends, CTV and OTT advertising, brand growth through DSP.' />               
//       </Helmet>
//       <div
//         className="relative h-96 bg-cover bg-center"
//         style={{ backgroundImage: `url("/assets/blog.jpeg")` }}
//       >
//         {/* Overlay */}
//         <div className="absolute inset-0 bg-black opacity-50"></div>

//         {/* Content */}
//         <div className="relative z-10 h-full flex flex-col justify-center items-start pl-8 md:pl-16">
//           <h1 className="text-white text-3xl md:text-5xl font-bold mb-4 mt-16">
//             Resources 
//           </h1>
//           <p className="text-white text-lg md:text-xl mb-6">
//             Insights. Trends. Innovation. Stay ahead with the latest in programmatic advertising.
//           </p>
//         </div>
//       </div>
//       <div className="container mx-auto p-4 py-20">
//         <div className="flex flex-wrap">
//           {/* Blog Content */}
//           <div className="w-full md:w-3/4 pr-6 p-4 rounded-lg shadow-md">
//             {/* Category indicator if selected */}
//             {selectedCategory && (
//               <div className="mb-6 px-4 py-3 bg-blue-50 border-l-4 border-blue-600 rounded">
//                 <div className="flex items-center justify-between">
//                   <h2 className="text-xl font-semibold text-blue-800">Viewing: {selectedCategory}</h2>
//                   <button 
//                     onClick={() => handleCategoryClick(null)}
//                     className="px-3 py-1 bg-white text-blue-600 hover:text-blue-800 border border-blue-200 rounded-md text-sm"
//                   >
//                     View All Categories
//                   </button>
//                 </div>
//               </div>
//             )}
            
//             {loading ? (
//               <Skeleton height={300} count={3} />
//             ) : sortedBlogs.length === 0 ? (
//               <div className="text-center py-12 text-gray-600">
//                 <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
//                 </svg>
//                 <h3 className="mt-2 text-lg font-medium">No blogs found</h3>
//                 <p className="mt-1">We couldn't find any blogs in this category.</p>
//                 <div className="mt-6">
//                   <button 
//                     onClick={() => handleCategoryClick(null)} 
//                     className="text-sm font-medium text-blue-600 hover:text-blue-500"
//                   >
//                     View all blogs instead
//                   </button>
//                 </div>
//               </div>
//             ) : (
//               <>
//                 {sortedBlogs.map((blog) => (
//                   <motion.div
//                     key={blog.id || blog.content_id}
//                     className="mb-8"
//                     initial={{ opacity: 0, y: 50 }}
//                     animate={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.5 }}
//                   >
//                     <img 
//                       src={blog.content_image} 
//                       alt={blog.content_title} 
//                       className="w-full h-60 sm:h-72 md:h-80 lg:h-[28rem] object-contain mb-4 mt-12" 
//                     />
//                     <div className="flex items-center text-gray-500 text-sm mb-2">
//                       <span><i className="fa fa-comments font-bold text-lg">Published : </i> {blog.content_date}</span>
//                       {blog.content_category && (
//                         <span className="ml-4 px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs">
//                           {blog.content_category}
//                         </span>
//                       )}
//                     </div>
//                     <h2 className="text-3xl font-semibold mb-2">{blog.content_title}</h2>
//                     <div className="mt-6">
//                       <a href={`/blog/${blog.content_url}`} className="hover:underline text-white bg-gradient-to-r from-blue-600 to-purple-500 px-4 py-2 rounded-full">
//                         Read More
//                       </a>
//                     </div>
//                   </motion.div>
//                 ))}
                
//                 {/* Pagination Controls */}
//                 {totalPages > 1 && <Pagination />}
                
//                 <div className="text-center text-gray-600 mt-4">
//                   Showing page {currentPage} of {totalPages} ({totalBlogs} blogs)
//                 </div>
//               </>
//             )}
//           </div>

//           {/* Sidebar */}
//           <div className="w-full md:w-1/4 mt-10">
//             <div className="mb-8 bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
//               <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600 pb-2">Categories</h3>
//               <ul className="space-y-3">
//                 <li>
//                   <button
//                     onClick={() => handleCategoryClick(null)}
//                     className={`flex justify-between items-center w-full text-gray-700 hover:text-blue-600 font-medium py-2 px-4 rounded-lg transition-colors ${
//                       selectedCategory === null ? 'bg-blue-100 text-blue-600' : ''
//                     }`}
//                   >
//                     <span>All Categories</span>
//                     <span className="bg-gray-200 rounded-full px-2 py-1 text-xs text-gray-600">
//                       {totalBlogs}
//                     </span>
//                   </button>
//                 </li>
//                 {categories.length === 0 ? (
//                   <Skeleton count={5} height={30} />
//                 ) : (
//                   categories.map((category) => (
//                     <li key={category.name}>
//                       <button
//                         onClick={() => handleCategoryClick(category.name)}
//                         className={`flex justify-between items-center w-full text-gray-700 hover:text-blue-600 font-medium py-2 px-4 rounded-lg transition-colors ${
//                           selectedCategory === category.name ? 'bg-blue-100 text-blue-600' : ''
//                         }`}
//                       >
//                         <span>{category.name}</span>
//                         <span className="bg-gray-200 rounded-full px-2 py-1 text-xs text-gray-600">
//                           {category.count}
//                         </span>
//                       </button>
//                     </li>
//                   ))
//                 )}
//               </ul>
//             </div>

//             {/* Sidebar - New Blogs */}
//             <div className="mb-8 bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
//               <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600 pb-2">New Blogs</h3>
//               <ul>
//                 {sidebarLoading ? (
//                   <Skeleton count={3} height={80} />
//                 ) : (
//                   newBlogs.map((blog) => (
//                     <motion.li
//                       key={blog.id || blog.content_id}
//                       className="mb-4 flex"
//                       initial={{ opacity: 0, x: -30 }}
//                       animate={{ opacity: 1, x: 0 }}
//                       transition={{ duration: 0.5 }}
//                     >
//                       <a href={`/blog/${blog.content_url}`} className="flex items-start w-full">
//                         <img src={blog.content_image} alt={blog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
//                         <div>
//                           <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors">{blog.content_title}</h4>
//                           <p className="text-gray-500 text-sm">{blog.content_date}</p>
//                         </div>
//                       </a>
//                     </motion.li>
//                   ))
//                 )}
//               </ul>
//             </div>

//             {/* Sidebar - Popular Posts */}
//             <div className="bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
//               <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600 pb-2">Popular Posts</h3>
//               <ul>
//                 {sidebarLoading ? (
//                   <Skeleton count={3} height={80} />
//                 ) : (
//                   popularBlogs.map((blog) => (
//                     <motion.li
//                       key={blog.id || blog.content_id}
//                       className="mb-4 flex"
//                       initial={{ opacity: 0, x: -30 }}
//                       animate={{ opacity: 1, x: 0 }}
//                       transition={{ duration: 0.5 }}
//                     >
//                       <a href={`/blog/${blog.content_url}`} className="flex items-start w-full">
//                         <img src={blog.content_image} alt={blog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
//                         <div>
//                           <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors">{blog.content_title}</h4>
//                           <p className="text-gray-500 text-sm">{blog.content_date}</p>
//                         </div>
//                       </a>
//                     </motion.li>
//                   ))
//                 )}
//               </ul>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default BlogMain;

// import React, { useState, useEffect } from "react";
// import Skeleton from "react-loading-skeleton";
// import 'react-loading-skeleton/dist/skeleton.css';
// import { motion } from "framer-motion";
// import Http from "../../Http";
// import { Blog_API } from "../../Helper";
// import { Helmet } from "react-helmet";

// const BlogMain = () => {
//   const [blogs, setBlogs] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(0);
//   const [totalBlogs, setTotalBlogs] = useState(0);
//   const blogsPerPage = 9; // Default blogs per page based on your API

//   useEffect(() => {
//     fetchBlogs(currentPage);
//   }, [currentPage, selectedCategory]);

//   // Fetch blogs with pagination
//   const fetchBlogs = async (page) => {
//     setLoading(true);
//     try {
//       // Add pagination parameter to API call
//       const endpoint = selectedCategory 
//         ? `${Blog_API}?page=${page}&category=${encodeURIComponent(selectedCategory)}`
//         : `${Blog_API}?page=${page}`;
        
//       const data = await Http.get(endpoint);
      
//       setBlogs(data.blogs);
//       setTotalBlogs(data.total_count || 0);
      
//       // Calculate total pages based on total blogs and blogs per page
//       const calculatedTotalPages = Math.ceil((data.total_count || 0) / blogsPerPage);
//       setTotalPages(calculatedTotalPages > 0 ? calculatedTotalPages : 1);

//       // Extract categories only on first load or when needed
//       if (categories.length === 0) {
//         const categoryMap = {};
//         data.blogs.forEach(blog => {
//           const category = blog.content_category;
//           if (category) {
//             categoryMap[category] = (categoryMap[category] || 0) + 1;
//           }
//         });
//         const categoryArray = Object.keys(categoryMap).map(category => ({
//           name: category,
//           count: categoryMap[category]
//         }));
//         setCategories(categoryArray);
//       }
//     } catch (error) {
//       console.error("Error fetching blogs:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Handle category click and filtering of blogs
//   const handleCategoryClick = (categoryName) => {
//     setSelectedCategory(categoryName);
//     setCurrentPage(1); // Reset to first page when changing category
//   };

//   // Handle page change
//   const handlePageChange = (page) => {
//     setCurrentPage(page);
//     // Scroll to top when changing page
//     window.scrollTo({
//       top: 0,
//       behavior: 'smooth'
//     });
//   };

//   // Get filteredBlogs sorted by date
//   const filteredBlogs = blogs
//     .sort((a, b) => new Date(b.content_date) - new Date(a.content_date));

//   // Pagination component
//   const Pagination = () => {
//     const pageNumbers = [];
//     const maxPageButtons = 5; // Max number of page buttons to show

//     // Create array of visible page numbers
//     let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
//     let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
//     // Adjust if we're near the end
//     if (endPage - startPage + 1 < maxPageButtons) {
//       startPage = Math.max(1, endPage - maxPageButtons + 1);
//     }

//     for (let i = startPage; i <= endPage; i++) {
//       pageNumbers.push(i);
//     }

//     return (
//       <div className="flex justify-center mt-8 mb-4">
//         <nav className="flex items-center">
//           <button
//             onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
//             disabled={currentPage === 1}
//             className={`px-3 py-2 rounded-l-md border ${
//               currentPage === 1 
//                 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
//                 : 'bg-white text-blue-600 hover:bg-blue-50'
//             }`}
//             aria-label="Previous Page"
//           >
//             <span className="sr-only">Previous</span>
//             <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
//             </svg>
//           </button>

//           {/* Show first page and ellipsis if not near start */}
//           {startPage > 1 && (
//             <>
//               <button
//                 onClick={() => handlePageChange(1)}
//                 className={`px-4 py-2 border bg-white text-blue-600 hover:bg-blue-50`}
//               >
//                 1
//               </button>
//               {startPage > 2 && (
//                 <span className="px-3 py-2 border bg-white text-gray-500">...</span>
//               )}
//             </>
//           )}

//           {/* Page numbers */}
//           {pageNumbers.map(number => (
//             <button
//               key={number}
//               onClick={() => handlePageChange(number)}
//               className={`px-4 py-2 border ${
//                 currentPage === number
//                   ? 'bg-blue-600 text-white border-blue-600'
//                   : 'bg-white text-blue-600 hover:bg-blue-50'
//               }`}
//             >
//               {number}
//             </button>
//           ))}

//           {/* Show last page and ellipsis if not near end */}
//           {endPage < totalPages && (
//             <>
//               {endPage < totalPages - 1 && (
//                 <span className="px-3 py-2 border bg-white text-gray-500">...</span>
//               )}
//               <button
//                 onClick={() => handlePageChange(totalPages)}
//                 className={`px-4 py-2 border bg-white text-blue-600 hover:bg-blue-50`}
//               >
//                 {totalPages}
//               </button>
//             </>
//           )}

//           <button
//             onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
//             disabled={currentPage === totalPages}
//             className={`px-3 py-2 rounded-r-md border ${
//               currentPage === totalPages 
//                 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
//                 : 'bg-white text-blue-600 hover:bg-blue-50'
//             }`}
//             aria-label="Next Page"
//           >
//             <span className="sr-only">Next</span>
//             <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
//             </svg>
//           </button>
//         </nav>
//       </div>
//     );
//   };

//   return (
//     <>
//       <Helmet>
//         <title>BidBox AI Blog – Insights on DSP and Advertising Trends</title>
//         <meta name='description' content='Stay updated with the latest trends in DSP technology and targeted advertising through BidBox AIs expert blog articles and insights.' />
//         <meta name='keywords' content='Programmatic advertising blog, DSP industry trends, digital marketing insights, ad tech news, AI in advertising, media buying strategies, advertising automation, real-time bidding updates, audience targeting tips, data-driven marketing, digital ad strategy, performance marketing techniques, online advertising trends, CTV and OTT advertising, brand growth through DSP.' />               
//       </Helmet>
//       <div
//         className="relative h-96 bg-cover bg-center"
//         style={{ backgroundImage: `url("/assets/blog.jpeg")` }}
//       >
//         {/* Overlay */}
//         <div className="absolute inset-0 bg-black opacity-50"></div>

//         {/* Content */}
//         <div className="relative z-10 h-full flex flex-col justify-center items-start pl-8 md:pl-16">
//           <h1 className="text-white text-3xl md:text-5xl font-bold mb-4 mt-16">
//             Resources 
//           </h1>
//           <p className="text-white text-lg md:text-xl mb-6">
//             Insights. Trends. Innovation. Stay ahead with the latest in programmatic advertising.
//           </p>
//         </div>
//       </div>
//       <div className="container mx-auto p-4 py-20">
//         <div className="flex flex-wrap">
//           {/* Blog Content */}
//           <div className="w-full md:w-3/4 pr-6 p-4 rounded-lg shadow-md">
//             {loading ? (
//               <Skeleton height={300} count={3} />
//             ) : filteredBlogs.length === 0 ? (
//               <div>No blogs found for this category.</div>
//             ) : (
//               <>
//                 {filteredBlogs.map((blog) => (
//                   <motion.div
//                     key={blog.id || blog.content_id}
//                     className="mb-8"
//                     initial={{ opacity: 0, y: 50 }}
//                     animate={{ opacity: 1, y: 0 }}
//                     transition={{ duration: 0.5 }}
//                   >
//                     <img 
//                       src={blog.content_image} 
//                       alt={blog.content_title} 
//                       className="w-full h-60 sm:h-72 md:h-80 lg:h-[28rem] object-contain mb-4 mt-12" 
//                     />
//                     <div className="flex items-center text-gray-500 text-sm mb-2">
//                       <span><i className="fa fa-comments font-bold text-lg">Published : </i> {blog.content_date}</span>
//                     </div>
//                     <h2 className="text-3xl font-semibold mb-2">{blog.content_title}</h2>
//                     <div className="mt-6">
//                       <a href={`/blog/${blog.content_url}`} className="hover:underline text-white bg-gradient-to-r from-blue-600 to-purple-500 px-4 py-2 rounded-full">
//                         Read More
//                       </a>
//                     </div>
//                   </motion.div>
//                 ))}
                
//                 {/* Pagination Controls */}
//                 {totalPages > 1 && <Pagination />}
                
//                 <div className="text-center text-gray-600 mt-4">
//                   Showing page {currentPage} of {totalPages} ({totalBlogs} blogs)
//                 </div>
//               </>
//             )}
//           </div>

//           {/* Sidebar */}
//           <div className="w-full md:w-1/4 mt-10">
//             <div className="mb-8 bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
//               <h3 className="text-xl font-bold mb-4 border-b-2 border-pblue-600 pb-2">Categories</h3>
//               <ul className="space-y-3">
//                 <li>
//                   <button
//                     onClick={() => handleCategoryClick(null)}
//                     className={`flex justify-between items-center text-gray-700 hover:text-blue-600 font-medium py-2 px-4 rounded-lg transition-colors ${
//                       selectedCategory === null ? 'bg-blue-100 text-blue-600' : ''
//                     }`}
//                   >
//                     All Categories
//                   </button>
//                 </li>
//                 {loading && categories.length === 0 ? (
//                   <Skeleton count={5} height={30} />
//                 ) : (
//                   categories.map((category) => (
//                     <li key={category.name}>
//                       <button
//                         onClick={() => handleCategoryClick(category.name)}
//                         className={`flex justify-between items-center text-gray-700 hover:text-blue-600 font-medium py-2 px-1 rounded-lg transition-colors ${
//                           selectedCategory === category.name ? 'bg-blue-100 text-blue-600' : ''
//                         }`}
//                       >
//                         <span>{category.name}</span>
//                         {/* <span className="bg-gray-200 rounded-full px-2 py-1 text-xs text-gray-600 ml-1">
//                           {category.count}
//                         </span> */}
//                       </button>
//                     </li>
//                   ))
//                 )}
//               </ul>
//             </div>

//             {/* Sidebar - New Blogs */}
//             <div className="mb-8 bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
//               <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600 pb-2">New Blogs</h3>
//               <ul>
//                 {loading ? (
//                   <Skeleton count={3} height={80} />
//                 ) : (
//                   blogs
//                     .sort((a, b) => new Date(b.content_date) - new Date(a.content_date))
//                     .slice(0, 3)
//                     .map((blog) => (
//                       <motion.li
//                         key={blog.id || blog.content_id}
//                         className="mb-4 flex"
//                         initial={{ opacity: 0, x: -30 }}
//                         animate={{ opacity: 1, x: 0 }}
//                         transition={{ duration: 0.5 }}
//                       >
//                         <a href={`/blog/${blog.content_url}`} className="flex items-start w-full">
//                           <img src={blog.content_image} alt={blog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
//                           <div>
//                             <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors">{blog.content_title}</h4>
//                             <p className="text-gray-500 text-sm">{blog.content_date}</p>
//                           </div>
//                         </a>
//                       </motion.li>
//                     ))
//                 )}
//               </ul>
//             </div>

//             {/* Sidebar - Popular Posts */}
//             <div className="bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
//               <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600 pb-2">Popular Posts</h3>
//               <ul>
//                 {loading ? (
//                   <Skeleton count={3} height={80} />
//                 ) : (
//                   blogs.slice(0, 10).map((blog) => (
//                     <motion.li
//                       key={blog.id || blog.content_id}
//                       className="mb-4 flex"
//                       initial={{ opacity: 0, x: -30 }}
//                       animate={{ opacity: 1, x: 0 }}
//                       transition={{ duration: 0.5 }}
//                     >
//                       <a href={`/blog/${blog.content_url}`} className="flex items-start w-full">
//                         <img src={blog.content_image} alt={blog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
//                         <div>
//                           <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors">{blog.content_title}</h4>
//                           <p className="text-gray-500 text-sm">{blog.content_date}</p>
//                         </div>
//                       </a>
//                     </motion.li>
//                   ))
//                 )}
//               </ul>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default BlogMain;


// import React, { useState, useEffect } from "react";
// import Skeleton from "react-loading-skeleton";
// import 'react-loading-skeleton/dist/skeleton.css'; // Import skeleton CSS
// import { motion } from "framer-motion"; // For animations
// import Http from "../../Http";
// import { Blog_API } from "../../Helper";
// import { Helmet } from "react-helmet";
// const BlogMain = () => {
//   const [blogs, setBlogs] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [selectedCategory, setSelectedCategory] = useState(null); // Track selected category
//   const [loading, setLoading] = useState(true); // Loading state

//   useEffect(() => {
//     // Fetch the blog data from API
//     const fetchBlogs = async () => {
//       // const response = await fetch("https://zonemedia.co/bidbox-blog/react-app-directory/blog-api.php");
//       // const data = await response.json();
//       try {
//         const data = await Http.get(Blog_API);
//       setBlogs(data.blogs);

//       // Extract categories from blogs for sidebar
//       const categoryMap = {};
//       data.blogs.forEach(blog => {
//         const category = blog.content_category;
//         if (category) {
//           categoryMap[category] = (categoryMap[category] || 0) + 1;
//         }
//       });
//       const categoryArray = Object.keys(categoryMap).map(category => ({
//         name: category,
//         count: categoryMap[category]
//       }));
//       setCategories(categoryArray);
//     } catch (error) {
//       console.error("Error fetching blogs:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//     fetchBlogs();
//   }, []);

//   // Handle category click and filtering of blogs
//   const handleCategoryClick = (categoryName) => {
//     setSelectedCategory(categoryName);
//   };

//   // Filter blogs by selected category and sort by date (newest first)
//   const filteredBlogs = blogs
//     .filter((blog) => selectedCategory === null || blog.content_category === selectedCategory)
//     .sort((a, b) => new Date(b.content_date) - new Date(a.content_date)); // Sort by date, newest first

//   return (
//     <>
//       <Helmet>
//                 <title>BidBox AI Blog – Insights on DSP and Advertising Trends</title>
//                 <meta name='description' content='Stay updated with the latest trends in DSP technology and targeted advertising through BidBox AIs expert blog articles and insights.' />
//                 <meta name='keywords' content='Programmatic advertising blog, DSP industry trends, digital marketing insights, ad tech news, AI in advertising, media buying strategies, advertising automation, real-time bidding updates, audience targeting tips, data-driven marketing, digital ad strategy, performance marketing techniques, online advertising trends, CTV and OTT advertising, brand growth through DSP.' />               
//             </Helmet>
//      <div
//   className="relative h-96 bg-cover bg-center"
//   style={{ backgroundImage: `url("/assets/blog.jpeg")` }}
// >
//       {/* Overlay */}
//       <div className="absolute inset-0 bg-black opacity-50" ></div>

//       {/* Content */}
//       <div className="relative z-10 h-full flex flex-col justify-center items-start pl-8 md:pl-16">
//         <h1 className="text-white text-3xl md:text-5xl font-bold mb-4 mt-16">
//            Resources 
//         </h1>
//         <p className="text-white text-lg md:text-xl mb-6">
//         Insights. Trends. Innovation. Stay ahead with the latest in programmatic advertising.
//         </p>
//         {/* <a href="#" className="text-blue-500 border border-blue-500 hover:bg-blue-500 hover:text-white text-lg font-semibold px-6 py-2 rounded transition duration-300" >
//           Free Consultation
//         </a> */}
//       </div>
//     </div>
//     <div className="container mx-auto p-4 py-20">
//       <div className="flex flex-wrap ">
//         {/* Blog Content */}
//         <div className="w-full md:w-3/4 pr-6  p-4 rounded-lg shadow-md ">
//           {loading ? (
//             <Skeleton height={300} count={3} />
//           ) : filteredBlogs.length === 0 ? (
//             <div>No blogs found for this category.</div>
//           ) : (
//             filteredBlogs.map((blog) => (
//               <motion.div
//                 key={blog.id}
//                 className="mb-8"
//                 initial={{ opacity: 0, y: 50 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ duration: 0.5 }}
//               >
//                 {/* <img src={blog.content_image} alt={blog.content_title} className="w-full h-96 object-cover mb-4 mt-12" /> */}
              
//                 <img 
//   src={blog.content_image} 
//   alt={blog.content_title} 
//   className="w-full h-60 sm:h-72 md:h-80 lg:h-[28rem] object-contain mb-4 mt-12" 
// />
//  <div className="flex items-center text-gray-500 text-sm mb-2 ">
//                   <span><i className="fa fa-comments font-bold text-lg">Published : </i> {blog.content_date}</span>
//                 </div>
//                 <h2 className="text-3xl font-semibold mb-2">{blog.content_title}</h2>
//                 <div className="mt-6">
//                 <a href={`/blog/${blog.content_url}`} className="hover:underline text-white  bg-gradient-to-r from-blue-600 to-purple-500 px-4 py-2 rounded-full">
//                   Read More
//                 </a>
//                 </div>
//               </motion.div>
//             ))
//           )}
//         </div>

//         {/* Sidebar */}
//         <div className="w-full md:w-1/4 mt-10">
//           <div className="mb-8 bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
//             <h3 className="text-xl font-bold mb-4 border-b-2 border-pblue-600 pb-2">Categories</h3>
//             <ul className="space-y-3">
//               <li>
//                 <button
//                   onClick={() => setSelectedCategory(null)}
//                   className={`flex justify-between items-center text-gray-700 hover:text-blue-600 font-medium py-2 px-4 rounded-lg transition-colors ${
//                     selectedCategory === null ? 'bg-blue-100 text-blue-600' : ''
//                   }`}
//                 >
//                   All Categories
//                 </button>
//               </li>
//               {loading ? (
//                 <Skeleton count={5} height={30} />
//               ) : (
//                 categories.map((category) => (
//                   <li key={category.name}>
//                     <button
//                       onClick={() => handleCategoryClick(category.name)}
//                       className={`flex justify-between items-center text-gray-700 hover:text-blue-600 font-medium py-2 px-1 rounded-lg transition-colors ${
//                         selectedCategory === category.name ? 'bg-blue-100 text-blue-600' : ''
//                       }`}
//                     >
//                       <span>{category.name}</span>
//                       <span className="bg-gray-200 rounded-full px-2 py-1 text-xs text-gray-600 ml-1">
//                         {category.count}
//                       </span>
//                     </button>
//                   </li>
//                 ))
//               )}
//             </ul>
//           </div>

//           {/* Sidebar - New Blogs */}
//           <div className="mb-8 bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
//             <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600 pb-2">New Blogs</h3>
//             <ul>
//               {loading ? (
//                 <Skeleton count={3} height={80} />
//               ) : (
//                 blogs
//                   .sort((a, b) => new Date(b.content_date) - new Date(a.content_date)) // Sort by date (newest first)
//                   .slice(0, 3) // Show only the latest 3 blogs
//                   .map((blog) => (
//                     <motion.li
//                       key={blog.id}
//                       className="mb-4 flex"
//                       initial={{ opacity: 0, x: -30 }}
//                       animate={{ opacity: 1, x: 0 }}
//                       transition={{ duration: 0.5 }}
//                     >
//                       <a href={`/blog/${blog.content_url}`} className="flex items-start w-full">
//                         <img src={blog.content_image} alt={blog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
//                         <div>
//                           <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors">{blog.content_title}</h4>
//                           <p className="text-gray-500 text-sm">{blog.content_date}</p>
//                         </div>
//                       </a>
//                     </motion.li>
//                   ))
//               )}
//             </ul>
//           </div>

//           {/* Sidebar - Popular Posts */}
//           <div className="bg-gray-100 p-4 rounded-lg shadow-lg ml-2">
//             <h3 className="text-xl font-bold mb-4 border-b-2 border-blue-600 pb-2">Popular Posts</h3>
//             <ul>
//               {loading ? (
//                 <Skeleton count={3} height={80} />
//               ) : (
//                 blogs.slice(0, 10).map((blog) => (
//                   <motion.li
//                     key={blog.id}
//                     className="mb-4 flex"
//                     initial={{ opacity: 0, x: -30 }}
//                     animate={{ opacity: 1, x: 0 }}
//                     transition={{ duration: 0.5 }}
//                   >
//                     <a href={`/blog/${blog.content_url}`} className="flex items-start w-full">
//                       <img src={blog.content_image} alt={blog.content_title} className="w-16 h-16 object-cover mr-4 rounded-lg" />
//                       <div>
//                         <h4 className="font-semibold text-gray-800 hover:text-blue-600 transition-colors">{blog.content_title}</h4>
//                         <p className="text-gray-500 text-sm">{blog.content_date}</p>
//                       </div>
//                     </a>
//                   </motion.li>
//                 ))
//               )}
//             </ul>
//           </div>
//         </div>
//       </div>
//     </div>
//     </>
//   );
// };

// export default BlogMain;